// Report Composer — 순수 트리 뮤테이션 (Phase 3b-1)
//
// ReportTemplate 노드 트리에 대한 불변(immutable) 편집 연산. 입력 트리는 절대 변경하지 않고
// 항상 새 트리를 반환한다. admin Composer Studio의 편집 조작(추가/삭제/순서이동/속성편집)이
// 이 함수들 위에서 동작한다.
//
// findNodeById(admin)와 동일하게 재귀 범위를 일치시킨다:
//   - group.children
//   - repeat.template (GroupNode)
//   - table cells + cell.content (중첩 노드 배열)
//
// ⚠️ 셀 자체(TableCell)/repeat.template/table 노드 자체는 ComposerNode 컨테이너의 "형제 배열"이
//    아니므로, 순서 이동(moveNodeInSiblings)·삽입(insertNode)·제거(removeNode)의 대상 단위는
//    어디까지나 "ComposerNode 배열의 원소"다(body / group.children / cell.content).
//    repeat.template(단일 그룹)과 table cell 자체는 제거/이동 대상에서 제외된다(아래 각 함수 문서 참조).

import type {
  ReportTemplate,
  ComposerNode,
  GroupNode,
  StackNode,
  TableNode,
  TableCell,
  TableCellStyle,
  TablePolicy,
  TableRepeatRegion,
  Binding,
  TextNode,
  RectNode,
  ImageNode,
  LineNode,
  SpacerNode,
  CheckboxNode,
  ChoiceGroupNode,
  SpecialNode,
  SpecialKind,
  BranchNode,
  BranchSlot,
  ConditionBinding,
  RepeatNode,
} from '@safetyhub/shared';
import type { ApprovalBlockConfig } from './approval/types';

/**
 * 새 표 셀의 기본 스타일 — 실선 테두리 + 패딩(상하 0.26mm / 좌우 1.06mm, 구 SMS 표 셀 동형)
 * + 폰트 크기 14.6667px(11pt, 정보표 동형). 폰트 패밀리는 기본(미지정, 캔버스 기본 상속).
 * 셀별/일괄 편집기 및 "기본값으로 초기화"에서 재사용. 매 호출 새 객체(공유 참조 방지).
 */
export const defaultCellStyle = (): TableCell['style'] => ({
  border: '1px solid #000',
  // 구 SMS 표 셀 동형: 상하 0.26mm(0.983px) / 좌우 1.06mm(4.006px) (1px/4px @96dpi)
  padding: { top: 0.983, right: 4.006, bottom: 0.983, left: 4.006 },
  fontSize: 14.6667, // 11pt × 96/72 ≈ 14.6667px (정보표 11pt 동형)
});
const newCellStyle = defaultCellStyle;

// ============================================================
// 셀 속성 상속 헬퍼 — 행/열 추가 시 인접 셀 style + 정렬 승계
// ============================================================
//
// addRow/addCol로 새 빈 셀을 만들 때, 고정 기본 스타일(defaultCellStyle) 대신
// 인접한 기존 셀의 style(깊은 복사)과 정렬(contentAlignH/contentAlignV, 값 복사)을 상속해
// 표 전체 표현(패딩·폰트·정렬 등)을 유지한다.
// 콘텐츠 필드(text/binding/aggregate/content)는 상속하지 않고 빈 셀로 둔다 — 표현 속성만 승계.

/** 새 빈 셀이 인접 셀에서 상속하는 표현 속성 묶음(style + 정렬). */
interface InheritedCellProps {
  style: TableCell['style'];
  contentAlignH?: TableCell['contentAlignH'];
  contentAlignV?: TableCell['contentAlignV'];
}

/** style(및 padding 중첩)을 참조 공유 없이 깊은 복사. undefined면 undefined(스타일 없음 상속). */
function cloneCellStyle(style: TableCell['style']): TableCell['style'] {
  if (!style) return undefined;
  const next: TableCellStyle = { ...style };
  if (style.padding) next.padding = { ...style.padding };
  return next;
}

/**
 * 참조 셀에서 표현 속성(style 깊은 복사 + 정렬 값 복사)을 뽑는다.
 * 정렬은 단순 문자열 필드라 값 복사로 충분. undefined면 키를 두지 않는다(불필요 undefined 키 방지).
 */
function pickCellProps(cell: TableCell): InheritedCellProps {
  const props: InheritedCellProps = { style: cloneCellStyle(cell.style) };
  if (cell.contentAlignH !== undefined) props.contentAlignH = cell.contentAlignH;
  if (cell.contentAlignV !== undefined) props.contentAlignV = cell.contentAlignV;
  return props;
}

/** 상속 속성 묶음을 참조 공유 없이 복제(style 깊은 복사 + 정렬 값 복사). 새 행/열 직전 셀 체인용. */
function cloneCellProps(props: InheritedCellProps): InheritedCellProps {
  const next: InheritedCellProps = { style: cloneCellStyle(props.style) };
  if (props.contentAlignH !== undefined) next.contentAlignH = props.contentAlignH;
  if (props.contentAlignV !== undefined) next.contentAlignV = props.contentAlignV;
  return next;
}

/** 상속 속성을 새 빈 셀(text:'')에 적용. 정렬은 undefined면 키 생략(기존 셀 객체 컨벤션). */
function buildInheritedCell(row: number, col: number, props: InheritedCellProps): TableCell {
  const cell: TableCell = { row, col, text: '', style: props.style };
  if (props.contentAlignH !== undefined) cell.contentAlignH = props.contentAlignH;
  if (props.contentAlignV !== undefined) cell.contentAlignV = props.contentAlignV;
  return cell;
}

/**
 * (refRow,refCol) 슬롯을 보유하거나 병합(rowspan/colspan)으로 덮는 셀을 찾아
 * 그 표현 속성(style 깊은 복사 + 정렬 값 복사)을 반환한다.
 * - found:true면 그 셀의 속성. style이 undefined면 그대로 상속(undefined).
 * - 그런 셀이 전혀 없으면(병합으로 슬롯이 비는 등) found:false → 호출 측 fallback.
 */
function inheritCellProps(
  table: TableNode,
  refRow: number,
  refCol: number,
): { found: boolean; props: InheritedCellProps } {
  const exact = table.cells.find((c) => c.row === refRow && c.col === refCol);
  if (exact) return { found: true, props: pickCellProps(exact) };
  // 정확한 좌표 셀이 없으면 병합 span이 그 슬롯을 덮는 셀을 찾는다.
  const covering = table.cells.find((c) => {
    const rs = c.rowspan ?? 1;
    const cs = c.colspan ?? 1;
    return c.row <= refRow && refRow < c.row + rs && c.col <= refCol && refCol < c.col + cs;
  });
  if (covering) return { found: true, props: pickCellProps(covering) };
  return { found: false, props: { style: undefined } };
}

/**
 * 인접 참조 셀을 못 찾았을 때 fallback으로 쓸 표현 속성(style 깊은 복사 + 정렬 값 복사).
 * - 스타일을 가진 셀이 있으면 그 셀의 속성(표 일관성), 없으면 첫 셀의 속성(undefined 상속),
 *   셀이 전혀 없으면 defaultCellStyle()만(정렬 없음).
 */
function fallbackCellProps(table: TableNode): InheritedCellProps {
  const styled = table.cells.find((c) => c.style);
  if (styled) return pickCellProps(styled);
  if (table.cells.length > 0) return pickCellProps(table.cells[0]);
  return { style: defaultCellStyle() };
}

// ============================================================
// 내부 재귀 헬퍼 — 노드 배열을 받아 변환된 새 배열을 반환
// ============================================================

/** 한 노드의 자식 배열(들)을 mapList로 변환해 새 노드를 반환(자식 없으면 원본 그대로) */
function mapChildren(node: ComposerNode, mapList: (nodes: ComposerNode[]) => ComposerNode[]): ComposerNode {
  switch (node.kind) {
    case 'group':
    case 'stack': {
      // group/stack 모두 children 배열을 보유하는 자식 컨테이너 — 동일 변환.
      const nextChildren = mapList(node.children);
      if (nextChildren === node.children) return node;
      return { ...node, children: nextChildren };
    }
    case 'repeat': {
      // repeat.template은 단일 GroupNode. 그 children을 재귀 변환한다.
      const nextTemplate = mapChildren(node.template, mapList) as GroupNode;
      if (nextTemplate === node.template) return node;
      return { ...node, template: nextTemplate };
    }
    case 'branch': {
      // 각 슬롯의 children 배열을 재귀 변환한다(group.children와 동일 취급).
      let changed = false;
      const nextBranches = node.branches.map((slot) => {
        const nextChildren = mapList(slot.children);
        if (nextChildren === slot.children) return slot;
        changed = true;
        return { ...slot, children: nextChildren };
      });
      if (!changed) return node;
      return { ...node, branches: nextBranches };
    }
    case 'table': {
      let changed = false;
      const nextCells = node.cells.map((cell) => {
        if (!cell.content) return cell;
        const nextContent = mapList(cell.content);
        if (nextContent === cell.content) return cell;
        changed = true;
        return { ...cell, content: nextContent };
      });
      if (!changed) return node;
      return { ...node, cells: nextCells };
    }
    default:
      return node;
  }
}

// ============================================================
// updateNode — id 노드에 patch 머지 (kind 불변 가정)
// ============================================================

/**
 * id에 매칭되는 노드에 patch를 얕게 머지한 새 트리를 반환한다.
 * kind는 불변으로 가정(patch에 kind를 넣어도 적용은 되나 호출 측이 보장).
 * id가 트리에 없으면 동일 트리(참조 동일)를 반환.
 */
export function updateNode(
  t: ReportTemplate,
  id: string,
  patch: Partial<ComposerNode>,
): ReportTemplate {
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.id === id) {
        changed = true;
        // patch를 머지(kind 불변 가정). union 좁힘을 위해 단언.
        return { ...node, ...patch } as ComposerNode;
      }
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

// ============================================================
// updateCell — table 노드의 (row,col) 셀에 patch 머지 (불변)
// ============================================================

/**
 * tableId인 table 노드를 트리에서 찾아, 그 cells에서 (row,col) 일치 셀에 patch를 얕게 머지한
 * 새 트리를 반환한다(불변). table은 group/repeat.template/cell.content 내 중첩 위치까지 재귀 탐색.
 *
 * - tableId가 없거나 그 노드가 table이 아니거나, (row,col) 셀이 없으면 동일 트리(참조 동일)를 반환.
 * - style은 호출 측이 통째 새 객체로 넘기는 것을 권장(얕은 머지라 부분 style 머지는 위험).
 */
export function updateCell(
  t: ReportTemplate,
  tableId: string,
  row: number,
  col: number,
  patch: Partial<TableCell>,
): ReportTemplate {
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.id === tableId && node.kind === 'table') {
        let cellChanged = false;
        const nextCells = node.cells.map((cell) => {
          if (cell.row === row && cell.col === col) {
            cellChanged = true;
            return { ...cell, ...patch };
          }
          return cell;
        });
        if (!cellChanged) return node; // 대상 셀 없음 → 무변경
        changed = true;
        return { ...node, cells: nextCells };
      }
      // 중첩(group/repeat.template/cell.content)에 table이 있을 수 있으므로 재귀.
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

// ============================================================
// updateCells — table 노드의 여러 (row,col) 셀에 patch 일괄 머지 (불변, 다중선택)
// ============================================================

/**
 * tableId인 table 노드를 트리에서 찾아, refs에 해당하는 각 셀에 patch를 머지한 새 트리를 반환한다(불변).
 *
 * - patch.style는 각 셀의 *기존 style에 머지*한다(`{ ...cell.style, ...patch.style }`). 셀마다 기존
 *   style이 다르므로 통째 교체가 아닌 머지가 필수(예: 글자색만 일괄 변경 시 각 셀의 배경/볼드 보존).
 *   patch.style의 키를 undefined로 넘기면 그 키만 해제된다(머지 후에도 undefined로 남음).
 * - 그 외 top-level 필드(header/align/text 등)는 교체(얕은 머지).
 * - refs가 비었거나 매칭되는 셀이 0개면 동일 트리(참조 동일)를 반환.
 * - tableId가 없거나 table이 아니면 동일 트리 반환. table은 중첩 위치까지 재귀 탐색.
 */
export function updateCells(
  t: ReportTemplate,
  tableId: string,
  refs: { row: number; col: number }[],
  patch: Partial<TableCell>,
): ReportTemplate {
  if (refs.length === 0) return t;
  // (row,col) 빠른 조회용 키 집합.
  const refKeys = new Set(refs.map((r) => `${r.row}::${r.col}`));
  const { style: patchStyle, ...rest } = patch;

  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.id === tableId && node.kind === 'table') {
        let cellChanged = false;
        const nextCells = node.cells.map((cell) => {
          if (!refKeys.has(`${cell.row}::${cell.col}`)) return cell;
          cellChanged = true;
          const merged: TableCell = { ...cell, ...rest };
          if (patchStyle !== undefined) {
            merged.style = { ...cell.style, ...patchStyle };
          }
          return merged;
        });
        if (!cellChanged) return node; // 매칭 셀 없음 → 무변경
        changed = true;
        return { ...node, cells: nextCells };
      }
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

/**
 * updateCells의 transform 버전 — tableId인 table 노드를 찾아, refs에 해당하는 각 셀을
 * fn으로 변환한 새 트리를 반환한다(불변).
 *
 * - updateCells가 patch를 얕게 머지하는 것과 달리, 매칭 셀마다 `fn(cell)`의 반환값으로 대체한다.
 *   fn은 새 TableCell을 반환해야 하며(불변 갱신), 입력 cell을 직접 변경하지 않는다.
 * - refs가 비었거나 매칭되는 셀이 0개면 동일 트리(참조 동일)를 반환.
 * - tableId가 없거나 table이 아니면 동일 트리 반환. table은 중첩 위치까지 재귀 탐색.
 */
export function mapCells(
  t: ReportTemplate,
  tableId: string,
  refs: { row: number; col: number }[],
  fn: (cell: TableCell) => TableCell,
): ReportTemplate {
  if (refs.length === 0) return t;
  const refKeys = new Set(refs.map((r) => `${r.row}::${r.col}`));

  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.id === tableId && node.kind === 'table') {
        let cellChanged = false;
        const nextCells = node.cells.map((cell) => {
          if (!refKeys.has(`${cell.row}::${cell.col}`)) return cell;
          cellChanged = true;
          return fn(cell);
        });
        if (!cellChanged) return node; // 매칭 셀 없음 → 무변경
        changed = true;
        return { ...node, cells: nextCells };
      }
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

// ============================================================
// 표 행/열 ops (T2) — 좌표 시프트 + span 보정 (모두 불변)
// ============================================================
//
// 좌표맵 모델(셀이 (row,col) 직접 보유, 병합은 span, 덮인 슬롯엔 셀 없음)에서
// 행/열을 추가·삭제할 때, 영향 받는 셀의 좌표를 시프트하고 가로지르는 병합 span을 보정한다.
// rowCount/colCount(최소 1 가드)와 colWidths(있으면 동기)를 함께 갱신한다.

/** tableId인 table 노드를 트리에서 찾아 transform을 적용한 새 트리를 반환(불변, 중첩 재귀). */
function mapTable(
  t: ReportTemplate,
  tableId: string,
  transform: (table: TableNode) => TableNode,
): ReportTemplate {
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.id === tableId && node.kind === 'table') {
        const nextTable = transform(node);
        if (nextTable === node) return node; // transform이 무변경 신호
        changed = true;
        return nextTable;
      }
      // 중첩(group/repeat.template/cell.content)에 table이 있을 수 있으므로 재귀.
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

/** rowspan(기본 1)이 atRow를 가로지르는지(시작 < atRow < 시작+span). 시작==atRow는 가로지름 아님. */
function rowSpanCrosses(cell: TableCell, atRow: number): boolean {
  const span = cell.rowspan ?? 1;
  return cell.row < atRow && cell.row + span > atRow;
}

/** colspan(기본 1)이 atCol을 가로지르는지. */
function colSpanCrosses(cell: TableCell, atCol: number): boolean {
  const span = cell.colspan ?? 1;
  return cell.col < atCol && cell.col + span > atCol;
}

/**
 * atRow 위치에 빈 행을 1개 삽입한다(불변).
 * - rowCount+1. row>=atRow인 셀은 row+1.
 * - atRow를 가로지르는 rowspan 셀은 rowspan+1(병합 영역 유지, 빈 셀 생성 생략).
 * - 그 외에는 atRow 행의 colCount개 슬롯 중, *가로지르는 병합에 덮이지 않은* 슬롯에만 빈 셀 신규.
 * - atRow는 [0, rowCount] 범위로 clamp(rowCount면 맨 끝 추가).
 */
/**
 * 행 삽입(at) 시 repeatRegions의 행 인덱스를 조정한다(불변).
 * - 규칙: 삽입 지점 `at` 이상의 인덱스는 +1. (`i>=at ? i+1 : i`)
 *   - at===rowStart → 새 행이 region 위(=region 아래로 밀림): rowStart/rowEnd 모두 +1.
 *   - rowStart<at<=rowEnd(내부 삽입) → rowEnd만 +1(패턴에 편입).
 *   - at>rowEnd(뒤 삽입) → 불변(=독립 행).
 * - headerRows 범위도 동일 규칙으로 shift.
 */
function shiftRepeatRegionsOnInsert(
  regions: TableRepeatRegion[],
  at: number,
): TableRepeatRegion[] {
  const bump = (i: number) => (i >= at ? i + 1 : i);
  return regions.map((r) => {
    const next: TableRepeatRegion = { ...r, rowStart: bump(r.rowStart), rowEnd: bump(r.rowEnd) };
    if (Array.isArray(r.headerRows)) {
      next.headerRows = [bump(r.headerRows[0]), bump(r.headerRows[1])];
    }
    return next;
  });
}

/**
 * 행 삭제(at) 시 repeatRegions의 행 인덱스를 조정한다(불변).
 * - rowStart: `s>at ? s-1 : s`. rowEnd: `e>=at ? e-1 : e`(삭제행이 region 내/뒤면 축소).
 * - 결과 rowEnd<rowStart(=region의 유일 행이 삭제돼 소멸)면 그 region은 drop.
 * - headerRows도 동일 규칙, 무효화되면 'none'.
 */
function shiftRepeatRegionsOnRemove(
  regions: TableRepeatRegion[],
  at: number,
): TableRepeatRegion[] {
  const out: TableRepeatRegion[] = [];
  for (const r of regions) {
    const rowStart = r.rowStart > at ? r.rowStart - 1 : r.rowStart;
    const rowEnd = r.rowEnd >= at ? r.rowEnd - 1 : r.rowEnd;
    if (rowEnd < rowStart) continue; // region 소멸
    const next: TableRepeatRegion = { ...r, rowStart, rowEnd };
    if (Array.isArray(r.headerRows)) {
      const h0 = r.headerRows[0] > at ? r.headerRows[0] - 1 : r.headerRows[0];
      const h1 = r.headerRows[1] >= at ? r.headerRows[1] - 1 : r.headerRows[1];
      next.headerRows = h1 < h0 ? 'none' : [h0, h1];
    }
    out.push(next);
  }
  return out;
}

export function addRow(
  t: ReportTemplate,
  tableId: string,
  atRow: number,
  opts: { fullWidth?: boolean } = {},
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    const at = Math.max(0, Math.min(atRow, table.rowCount));
    // 가로지르는 rowspan 셀이 덮는 열 집합(이 열엔 새 빈 셀 생성 생략)
    const coveredCols = new Set<number>();
    const shifted: TableCell[] = table.cells.map((cell) => {
      if (rowSpanCrosses(cell, at)) {
        const span = cell.colspan ?? 1;
        for (let c = cell.col; c < cell.col + span; c++) coveredCols.add(c);
        return { ...cell, rowspan: (cell.rowspan ?? 1) + 1 };
      }
      if (cell.row >= at) return { ...cell, row: cell.row + 1 };
      return cell;
    });
    // 새 셀 style·정렬은 인접한 기존 행(위 행 at-1, 맨 위 삽입이면 아래 행=원본 0)에서 상속.
    // 참조는 shift 전 원본 좌표계(table.cells)에서 찾는다.
    const refRow = at > 0 ? at - 1 : 0;
    const newCells: TableCell[] = [];
    if (opts.fullWidth && coveredCols.size === 0 && table.colCount > 1) {
      // 전체 폭 단일 셀 행 — 다단 표에 추가하는 독립(비반복) 행 등. col0에 colspan=colCount(1단 폭)
      // 단일 셀을 만들면, 렌더 엔진이 다단 영역 안의 "전체폭 셀"을 물리 전체 폭으로 colspan 확장한다.
      const ref = inheritCellProps(table, refRow, 0);
      const props = ref.found ? ref.props : fallbackCellProps(table);
      newCells.push({ ...buildInheritedCell(at, 0, props), colspan: table.colCount });
    } else {
      let prevProps: InheritedCellProps | undefined;
      let createdAny = false;
      for (let c = 0; c < table.colCount; c++) {
        if (coveredCols.has(c)) continue;
        const ref = inheritCellProps(table, refRow, c);
        let props: InheritedCellProps;
        if (ref.found) {
          props = ref.props;
        } else if (createdAny && prevProps) {
          props = cloneCellProps(prevProps); // ① 같은 새 행 직전 셀
        } else {
          props = fallbackCellProps(table); // ② 표 임의 셀 → ③ default
        }
        newCells.push(buildInheritedCell(at, c, props));
        prevProps = props;
        createdAny = true;
      }
    }
    const nextTable: TableNode = {
      ...table,
      rowCount: table.rowCount + 1,
      cells: [...shifted, ...newCells],
    };
    if (table.repeatRegions) {
      nextTable.repeatRegions = shiftRepeatRegionsOnInsert(table.repeatRegions, at);
    }
    if (table.rowHeights) {
      const rh = table.rowHeights.slice();
      rh.splice(at, 0, 'auto'); // 새 행은 auto
      nextTable.rowHeights = rh;
    }
    return nextTable;
  });
}

/**
 * atRow 행을 제거한다(불변).
 * - rowCount==1이면 삭제 거부(원본 반환).
 * - row==atRow 셀 제거. row>atRow는 row-1.
 * - atRow를 가로지르는 rowspan은 rowspan-1(1 미만이면 1로 clamp).
 */
export function removeRow(t: ReportTemplate, tableId: string, atRow: number): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    if (table.rowCount <= 1) return table; // 최소 1 가드
    if (atRow < 0 || atRow >= table.rowCount) return table;
    const nextCells: TableCell[] = [];
    for (const cell of table.cells) {
      if (cell.row === atRow) continue; // 해당 행 셀 제거
      if (rowSpanCrosses(cell, atRow)) {
        const nextSpan = Math.max(1, (cell.rowspan ?? 1) - 1);
        nextCells.push({ ...cell, rowspan: nextSpan });
        continue;
      }
      if (cell.row > atRow) {
        nextCells.push({ ...cell, row: cell.row - 1 });
        continue;
      }
      nextCells.push(cell);
    }
    const nextTable: TableNode = { ...table, rowCount: table.rowCount - 1, cells: nextCells };
    if (table.repeatRegions) {
      const shiftedRegions = shiftRepeatRegionsOnRemove(table.repeatRegions, atRow);
      if (shiftedRegions.length > 0) {
        nextTable.repeatRegions = shiftedRegions;
      } else {
        delete (nextTable as { repeatRegions?: unknown }).repeatRegions;
      }
    }
    if (table.rowHeights) {
      const rh = table.rowHeights.slice();
      rh.splice(atRow, 1);
      nextTable.rowHeights = rh;
    }
    return nextTable;
  });
}

/**
 * atCol 위치에 빈 열을 1개 삽입한다(불변).
 * - colCount+1. col>=atCol인 셀은 col+1.
 * - atCol을 가로지르는 colspan 셀은 colspan+1(빈 셀 생성 생략).
 * - 그 외 atCol 열의 rowCount개 슬롯 중 가로지르는 병합에 덮이지 않은 슬롯에만 빈 셀 신규.
 * - colWidths 있으면 atCol에 'auto' 삽입.
 * - atCol은 [0, colCount] 범위로 clamp.
 */
export function addCol(t: ReportTemplate, tableId: string, atCol: number): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    const at = Math.max(0, Math.min(atCol, table.colCount));
    const coveredRows = new Set<number>();
    const shifted: TableCell[] = table.cells.map((cell) => {
      if (colSpanCrosses(cell, at)) {
        const span = cell.rowspan ?? 1;
        for (let r = cell.row; r < cell.row + span; r++) coveredRows.add(r);
        return { ...cell, colspan: (cell.colspan ?? 1) + 1 };
      }
      if (cell.col >= at) return { ...cell, col: cell.col + 1 };
      return cell;
    });
    // 새 셀 style·정렬은 인접한 기존 열(왼쪽 열 at-1, 맨 왼쪽 삽입이면 오른쪽 열=원본 0)에서 상속.
    // 참조는 shift 전 원본 좌표계(table.cells)에서 찾는다.
    const refCol = at > 0 ? at - 1 : 0;
    const newCells: TableCell[] = [];
    let prevProps: InheritedCellProps | undefined;
    let createdAny = false;
    for (let r = 0; r < table.rowCount; r++) {
      if (coveredRows.has(r)) continue;
      const ref = inheritCellProps(table, r, refCol);
      let props: InheritedCellProps;
      if (ref.found) {
        props = ref.props;
      } else if (createdAny && prevProps) {
        props = cloneCellProps(prevProps); // ① 같은 새 열 직전 셀
      } else {
        props = fallbackCellProps(table); // ② 표 임의 셀 → ③ default
      }
      newCells.push(buildInheritedCell(r, at, props));
      prevProps = props;
      createdAny = true;
    }
    const nextTable: TableNode = {
      ...table,
      colCount: table.colCount + 1,
      cells: [...shifted, ...newCells],
    };
    if (table.colWidths) {
      const widths = table.colWidths.slice();
      widths.splice(at, 0, 'auto');
      nextTable.colWidths = widths;
    }
    return nextTable;
  });
}

/**
 * atCol 열을 제거한다(불변).
 * - colCount==1이면 삭제 거부(원본 반환).
 * - col==atCol 셀 제거. col>atCol는 col-1.
 * - atCol을 가로지르는 colspan은 colspan-1(1 미만이면 1로 clamp).
 * - colWidths 있으면 atCol 제거.
 */
export function removeCol(t: ReportTemplate, tableId: string, atCol: number): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    if (table.colCount <= 1) return table; // 최소 1 가드
    if (atCol < 0 || atCol >= table.colCount) return table;
    const nextCells: TableCell[] = [];
    for (const cell of table.cells) {
      if (cell.col === atCol) continue;
      if (colSpanCrosses(cell, atCol)) {
        const nextSpan = Math.max(1, (cell.colspan ?? 1) - 1);
        nextCells.push({ ...cell, colspan: nextSpan });
        continue;
      }
      if (cell.col > atCol) {
        nextCells.push({ ...cell, col: cell.col - 1 });
        continue;
      }
      nextCells.push(cell);
    }
    const nextTable: TableNode = {
      ...table,
      colCount: table.colCount - 1,
      cells: nextCells,
    };
    if (table.colWidths) {
      const widths = table.colWidths.slice();
      widths.splice(atCol, 1);
      nextTable.colWidths = widths;
    }
    return nextTable;
  });
}

/**
 * 열 너비(colWidths)를 통째 교체한다(불변).
 * 길이=colCount 권장이나 불일치도 허용(호출 측 책임). widths가 빈 배열이면 colWidths 제거.
 */
export function setColWidths(
  t: ReportTemplate,
  tableId: string,
  widths: (number | string)[],
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    if (widths.length === 0) {
      if (table.colWidths === undefined) return table;
      const rest = { ...table };
      delete rest.colWidths;
      return rest;
    }
    return { ...table, colWidths: widths.slice() };
  });
}

/** 행 높이 일괄 지정(빈 배열이면 rowHeights 제거 → 전부 auto). */
export function setRowHeights(
  t: ReportTemplate,
  tableId: string,
  heights: (number | string)[],
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    if (heights.length === 0) {
      if (table.rowHeights === undefined) return table;
      const rest = { ...table };
      delete rest.rowHeights;
      return rest;
    }
    return { ...table, rowHeights: heights.slice() };
  });
}

// ============================================================
// 표 셀 병합/분할 ops (T3) — "이웃 셀 흡수" 방식 (모두 불변)
// ============================================================
//
// 좌표맵 모델(셀이 (row,col) 직접 보유, 병합은 span, 덮인 슬롯엔 셀 없음)에서
// 선택 셀이 오른쪽/아래 인접 셀을 흡수해 span을 키운다(병합). 병합 해제는 span을 1로 되돌리고
// 덮였던 슬롯에 빈 셀을 재생성한다. 단순화를 위해 직교 span이 1일 때만 흡수를 허용한다
// (예: 우측 병합은 effRowspan==1, 아래 병합은 effColspan==1일 때만 — 들쭉날쭉한 병합 방지).

/** (row,col) 위치의 셀을 cells에서 찾는다(span에 덮인 슬롯이면 undefined). */
function findCellAt(table: TableNode, row: number, col: number): TableCell | undefined {
  return table.cells.find((c) => c.row === row && c.col === col);
}

/** 셀이 병합 없는 1×1인지(흡수 대상 자격). */
function isPlainCell(cell: TableCell): boolean {
  return (cell.rowspan ?? 1) === 1 && (cell.colspan ?? 1) === 1;
}

/**
 * (row,col) 셀이 오른쪽 인접 슬롯을 흡수할 수 있는지(병합 가능) 판정한다.
 * - 선택 셀이 존재하고 effRowspan==1(직교 span 1).
 * - 흡수 대상 (row, col+effColspan)이 colCount 안이고, 1×1 셀로 존재.
 */
export function canMergeCellRight(table: TableNode, row: number, col: number): boolean {
  const cell = findCellAt(table, row, col);
  if (!cell) return false;
  const effRowspan = cell.rowspan ?? 1;
  const effColspan = cell.colspan ?? 1;
  if (effRowspan !== 1) return false; // 직교 span은 1만 허용(단순화)
  const targetCol = col + effColspan;
  if (targetCol >= table.colCount) return false; // 경계
  const target = findCellAt(table, row, targetCol);
  return !!target && isPlainCell(target);
}

/**
 * (row,col) 셀이 아래 인접 슬롯을 흡수할 수 있는지(병합 가능) 판정한다.
 * - 선택 셀이 존재하고 effColspan==1.
 * - 흡수 대상 (row+effRowspan, col)이 rowCount 안이고, 1×1 셀로 존재.
 */
export function canMergeCellDown(table: TableNode, row: number, col: number): boolean {
  const cell = findCellAt(table, row, col);
  if (!cell) return false;
  const effRowspan = cell.rowspan ?? 1;
  const effColspan = cell.colspan ?? 1;
  if (effColspan !== 1) return false;
  const targetRow = row + effRowspan;
  if (targetRow >= table.rowCount) return false;
  const target = findCellAt(table, targetRow, col);
  return !!target && isPlainCell(target);
}

/** (row,col) 셀이 병합 해제 가능한지(span이 1×1보다 큰지). */
export function canUnmergeCell(table: TableNode, row: number, col: number): boolean {
  const cell = findCellAt(table, row, col);
  if (!cell) return false;
  return (cell.rowspan ?? 1) * (cell.colspan ?? 1) > 1;
}

/**
 * (row,col) 셀이 오른쪽 인접 슬롯을 흡수한다(colspan+1, 불변).
 * - 가드(canMergeCellRight) 불충족이면 원본 그대로 반환(no-op).
 * - 흡수 대상 셀 제거 + 선택 셀 colspan = effColspan+1.
 */
export function mergeCellRight(t: ReportTemplate, tableId: string, row: number, col: number): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    if (!canMergeCellRight(table, row, col)) return table;
    const cell = findCellAt(table, row, col)!;
    const effColspan = cell.colspan ?? 1;
    const targetCol = col + effColspan;
    const nextCells = table.cells
      .filter((c) => !(c.row === row && c.col === targetCol)) // 흡수 대상 제거
      .map((c) => (c.row === row && c.col === col ? { ...c, colspan: effColspan + 1 } : c));
    return { ...table, cells: nextCells };
  });
}

/**
 * (row,col) 셀이 아래 인접 슬롯을 흡수한다(rowspan+1, 불변).
 * - 가드(canMergeCellDown) 불충족이면 원본 그대로 반환(no-op).
 * - 흡수 대상 셀 제거 + 선택 셀 rowspan = effRowspan+1.
 */
export function mergeCellDown(t: ReportTemplate, tableId: string, row: number, col: number): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    if (!canMergeCellDown(table, row, col)) return table;
    const cell = findCellAt(table, row, col)!;
    const effRowspan = cell.rowspan ?? 1;
    const targetRow = row + effRowspan;
    const nextCells = table.cells
      .filter((c) => !(c.row === targetRow && c.col === col))
      .map((c) => (c.row === row && c.col === col ? { ...c, rowspan: effRowspan + 1 } : c));
    return { ...table, cells: nextCells };
  });
}

/**
 * (row,col) 셀의 병합을 해제한다(불변).
 * - span이 1×1이면 no-op(원본 반환).
 * - 선택 셀 rowspan/colspan을 1로(속성 자체 제거 — span=1 생략 규약 유지).
 * - 덮였던 슬롯 [row, row+effRowspan) × [col, col+effColspan) − (row,col)에 빈 셀 재생성
 *   (text:'').
 */
/**
 * (row,col) 셀의 병합을 푼다(TableNode 레벨, 불변). 슬롯 복원 로직의 정본 — mergeCellsRect에서도 재사용.
 * - span이 1×1이면 no-op(원본 반환).
 * - 선택 셀 rowspan/colspan 속성 제거 + 덮였던 슬롯에 빈 셀 재생성(text:'').
 */
function unmergeCellInTable(table: TableNode, row: number, col: number): TableNode {
  if (!canUnmergeCell(table, row, col)) return table;
  const cell = findCellAt(table, row, col)!;
  const effRowspan = cell.rowspan ?? 1;
  const effColspan = cell.colspan ?? 1;
  // 선택 셀 span 제거(rowspan/colspan 속성 자체를 뺀다).
  const cleared: TableCell = { ...cell };
  delete cleared.rowspan;
  delete cleared.colspan;
  // 덮였던 슬롯에 빈 셀 재생성.
  const newCells: TableCell[] = [];
  for (let rr = row; rr < row + effRowspan; rr++) {
    for (let cc = col; cc < col + effColspan; cc++) {
      if (rr === row && cc === col) continue;
      newCells.push({ row: rr, col: cc, text: '' });
    }
  }
  const nextCells = table.cells.map((c) => (c.row === row && c.col === col ? cleared : c));
  return { ...table, cells: [...nextCells, ...newCells] };
}

export function unmergeCell(t: ReportTemplate, tableId: string, row: number, col: number): ReportTemplate {
  return mapTable(t, tableId, (table) => unmergeCellInTable(table, row, col));
}

/**
 * 직사각 범위 [r0..r1] × [c0..c1]를 한 번에 병합 가능한지 판정한다(Excel/HWP 범위 병합).
 * - 범위가 2셀 이상(`r1>r0 || c1>c0`)일 것.
 * - 범위·표 경계 안에 있을 것(0 <= r0<=r1 < rowCount, 0 <= c0<=c1 < colCount).
 * 범위 내에 이미 병합된 셀이 끼어 있어도 허용한다(mergeCellsRect가 풀고 재병합).
 * 호출부에서 좌표를 정규화(min/max)해 전달하지만 내부에서도 방어적으로 검사한다.
 */
export function canMergeCellsRect(
  table: TableNode,
  r0: number,
  c0: number,
  r1: number,
  c1: number,
): boolean {
  if (r0 > r1 || c0 > c1) return false; // 비정규 좌표(방어)
  if (r1 <= r0 && c1 <= c0) return false; // 단일 셀(2셀 이상 아님)
  if (r0 < 0 || c0 < 0) return false; // 경계
  if (r1 >= table.rowCount || c1 >= table.colCount) return false; // 경계
  return true;
}

/**
 * 선택한 셀들(refs)이 그 bounding rect를 빈칸 없이 꽉 채우는지 판정한다.
 * - "수평/수직으로 연결되지 않은 셀들"(예: (0,0)과 (0,2)만 선택, 사이 (0,1) 미선택) 병합을 막는 게이트.
 * - 각 ref 셀의 점유 슬롯(병합 셀이면 rowspan/colspan 만큼)을 모두 모아 점유 집합을 만든다.
 *   존재하지 않는 ref(표에 셀이 없는 좌표)는 무시한다.
 * - 점유 집합의 bounding rect [r0..r1]×[c0..c1](min/max)를 산출.
 *   2셀 미만(단일 슬롯, r1===r0 && c1===c0)이면 병합 대상이 아니므로 false.
 * - rect 내 모든 슬롯이 점유 집합에 있으면 true, 하나라도 빠지면(빈칸) false.
 */
export function isRectFullySelected(
  table: TableNode,
  refs: { row: number; col: number }[],
): boolean {
  // 각 ref 셀의 점유 슬롯을 모은다(병합 셀은 span 영역 전체 점유).
  const occupied = new Set<string>();
  for (const ref of refs) {
    const cell = findCellAt(table, ref.row, ref.col);
    if (!cell) continue; // 존재하지 않는 ref 무시
    const rs = cell.rowspan ?? 1;
    const cs = cell.colspan ?? 1;
    for (let rr = cell.row; rr < cell.row + rs; rr++) {
      for (let cc = cell.col; cc < cell.col + cs; cc++) {
        occupied.add(`${rr}::${cc}`);
      }
    }
  }
  if (occupied.size === 0) return false;
  // 점유 집합의 bounding rect(min/max) 산출.
  let r0 = Infinity;
  let c0 = Infinity;
  let r1 = -Infinity;
  let c1 = -Infinity;
  for (const key of occupied) {
    const [rStr, cStr] = key.split('::');
    const r = Number(rStr);
    const c = Number(cStr);
    if (r < r0) r0 = r;
    if (r > r1) r1 = r;
    if (c < c0) c0 = c;
    if (c > c1) c1 = c;
  }
  if (r1 === r0 && c1 === c0) return false; // 단일 슬롯(2셀 미만)
  // rect 내 모든 슬롯이 점유되어야 한다(빈칸 없음).
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      if (!occupied.has(`${r}::${c}`)) return false;
    }
  }
  return true;
}

/**
 * 범위(r0,c0,r1,c1)와 교차하는 기존 병합 셀의 span을 모두 포함하도록 확장한다(fixpoint).
 * - 확장으로 또 다른 병합이 새로 걸릴 수 있으므로 안정될 때까지 반복한다.
 * - mergeCellsRect의 1단계 로직을 분리한 것 — 호출부(admin)가 병합 후 좌상단 anchor를 계산할 때도 재사용한다.
 */
export function expandMergeRect(
  table: TableNode,
  r0: number,
  c0: number,
  r1: number,
  c1: number,
): { r0: number; c0: number; r1: number; c1: number } {
  let R0 = r0;
  let C0 = c0;
  let R1 = r1;
  let C1 = c1;
  let changed = true;
  while (changed) {
    changed = false;
    for (const cell of table.cells) {
      const rs = cell.rowspan ?? 1;
      const cs = cell.colspan ?? 1;
      if (rs === 1 && cs === 1) continue; // 1×1은 확장 무관
      const cr0 = cell.row;
      const cr1 = cell.row + rs - 1;
      const cc0 = cell.col;
      const cc1 = cell.col + cs - 1;
      // 병합 셀 사각형이 현재 범위와 교차하는가.
      if (cr0 <= R1 && cr1 >= R0 && cc0 <= C1 && cc1 >= C0) {
        if (cr0 < R0) { R0 = cr0; changed = true; }
        if (cr1 > R1) { R1 = cr1; changed = true; }
        if (cc0 < C0) { C0 = cc0; changed = true; }
        if (cc1 > C1) { C1 = cc1; changed = true; }
      }
    }
  }
  return { r0: R0, c0: C0, r1: R1, c1: C1 };
}

/**
 * 직사각 범위 [r0..r1] × [c0..c1]를 좌상단 셀로 병합한다(불변, Excel/HWP식).
 * - 가드(canMergeCellsRect) 불충족이면 원본 그대로 반환(no-op).
 * - 범위에 걸친 기존 병합 셀이 범위 밖으로 삐져나오면, 그 span을 포함하도록 범위를 확장한다
 *   (확장으로 또 다른 병합이 새로 걸릴 수 있으므로 안정될 때까지 반복 = fixpoint).
 * - 안정된 범위 내 모든 기존 병합을 풀어 슬롯 셀을 복원(unmergeCellInTable 재사용)한 뒤,
 *   좌상단(R0,C0) 셀의 colspan/rowspan을 범위 크기로 설정하고 나머지 셀을 제거(=새 단일 병합).
 * - 내용은 좌상단 셀만 유지(나머지 셀 내용은 버림 — mergeCellRight와 동일 정책).
 */
export function mergeCellsRect(
  t: ReportTemplate,
  tableId: string,
  r0: number,
  c0: number,
  r1: number,
  c1: number,
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    if (!canMergeCellsRect(table, r0, c0, r1, c1)) return table;
    // 1. fixpoint 범위 확장 — 범위와 교차하는 병합 셀의 전체 span을 포함시킨다.
    const { r0: R0, c0: C0, r1: R1, c1: C1 } = expandMergeRect(table, r0, c0, r1, c1);
    // 2. 안정된 범위 내 모든 기존 병합 해제(슬롯 복원). 확장 후이므로 병합 anchor가 모두 범위 안.
    let work = table;
    const mergedAnchors = work.cells
      .filter(
        (c) =>
          (c.rowspan ?? 1) * (c.colspan ?? 1) > 1 &&
          c.row >= R0 && c.row <= R1 && c.col >= C0 && c.col <= C1,
      )
      .map((c) => ({ row: c.row, col: c.col }));
    for (const a of mergedAnchors) {
      work = unmergeCellInTable(work, a.row, a.col);
    }
    // 3. 이제 범위 내 모든 슬롯이 1×1. 좌상단 span 설정 + 나머지 제거.
    const nextColspan = C1 - C0 + 1;
    const nextRowspan = R1 - R0 + 1;
    const inRange = (cell: TableCell): boolean =>
      cell.row >= R0 && cell.row <= R1 && cell.col >= C0 && cell.col <= C1;
    const nextCells = work.cells
      // 범위 내 좌상단을 제외한 나머지 셀 제거(흡수).
      .filter((c) => !(inRange(c) && !(c.row === R0 && c.col === C0)))
      // 좌상단 셀 span 설정(span=1 생략 규약 유지 — 1이면 속성 자체를 두지 않음).
      .map((c) => {
        if (!(c.row === R0 && c.col === C0)) return c;
        const merged: TableCell = { ...c };
        if (nextColspan > 1) merged.colspan = nextColspan;
        else delete merged.colspan;
        if (nextRowspan > 1) merged.rowspan = nextRowspan;
        else delete merged.rowspan;
        return merged;
      });
    return { ...work, cells: nextCells };
  });
}

// ============================================================
// 표 반복영역(repeatRegion) + tablePolicy ops (T4) — 동적 표 (모두 불변)
// ============================================================
//
// 데이터 표는 repeatRegions로 rowStart~rowEnd 구간을 data[over] 배열 길이만큼 복제한다
// (렌더 엔진은 이미 지원). 여기서는 그 정의(tablePolicy + repeatRegions)를 편집하는
// 불변 ops만 제공한다 — 좌표/셀은 건드리지 않는다.

/** 표의 페이지 분할 정책(tablePolicy)을 교체한다(불변). 동일 값이면 무변경(참조 동일). */
export function setTablePolicy(
  t: ReportTemplate,
  tableId: string,
  policy: TablePolicy,
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    if (table.tablePolicy === policy) return table; // 무변경
    return { ...table, tablePolicy: policy };
  });
}

/**
 * 반복영역(TableRepeatRegion)을 추가한다(불변).
 * - 범위 검증: 0<=rowStart<=rowEnd<rowCount 가 아니면 거부(원본 반환, no-op).
 * - repeatRegions가 없으면 새 배열 생성, 있으면 끝에 push(복제).
 */
export function addRepeatRegion(
  t: ReportTemplate,
  tableId: string,
  region: TableRepeatRegion,
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    // 범위 검증(잘못된 영역은 거부)
    if (region.rowStart < 0 || region.rowStart > region.rowEnd || region.rowEnd >= table.rowCount) {
      return table; // no-op
    }
    const next = table.repeatRegions ? [...table.repeatRegions, { ...region }] : [{ ...region }];
    return { ...table, repeatRegions: next };
  });
}

/**
 * index 위치의 반복영역을 제거한다(불변).
 * - index 범위 밖이면 무변경(참조 동일).
 * - 제거 결과 배열이 비면 repeatRegions 속성 자체를 제거(undefined — 정적 표 규약 유지).
 */
export function removeRepeatRegion(
  t: ReportTemplate,
  tableId: string,
  index: number,
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    const regions = table.repeatRegions;
    if (!regions || index < 0 || index >= regions.length) return table; // no-op
    const next = regions.filter((_, i) => i !== index);
    if (next.length === 0) {
      const rest = { ...table };
      delete rest.repeatRegions;
      return rest;
    }
    return { ...table, repeatRegions: next };
  });
}

/**
 * index 위치의 반복영역에 patch를 얕게 머지한다(불변).
 * - index 범위 밖이면 무변경(참조 동일).
 * - over/rowStart/rowEnd 부분 갱신용(범위 재검증은 하지 않음 — 편집 중 임시 무효값 허용).
 */
export function updateRepeatRegion(
  t: ReportTemplate,
  tableId: string,
  index: number,
  patch: Partial<TableRepeatRegion>,
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    const regions = table.repeatRegions;
    if (!regions || index < 0 || index >= regions.length) return table; // no-op
    const next = regions.map((r, i) => (i === index ? { ...r, ...patch } : r));
    return { ...table, repeatRegions: next };
  });
}

/**
 * 반복영역 밴드 행 재지정 계획(순수 조회). newRowStart로 옮길 때 밴드 크기(rowEnd-rowStart)를 유지하며,
 * 옛 밴드의 매핑 바인딩(cell.binding)을 새 밴드의 같은 col 셀로 어떻게 처리할지 산출한다(데이터 파괴 금지).
 *  - moves: 대상 셀이 비었거나 정적 텍스트만(content/binding/aggregate/compute 없음)이면 바인딩을 그 셀로 이동
 *    (옛 셀은 정적으로 환원).
 *  - skips: 대상 셀이 이미 content/binding/aggregate/compute를 보유(또는 병합에 덮여 셀 자체가 없음)하면
 *    이동하지 않고 옛 셀의 바인딩만 제거(그 칼럼은 "미지정" — 운영자가 새 행에서 다시 지정).
 * 반환 null = regionIndex 무효 또는 새 밴드가 표 범위를 벗어남(no-op).
 */
export interface RepeatRowReassignPlan {
  newRowStart: number;
  newRowEnd: number;
  moves: { fromRow: number; fromCol: number; toRow: number; toCol: number; binding: Binding }[];
  skips: { fromRow: number; fromCol: number }[];
}

export function planRepeatRegionRowReassign(
  table: TableNode,
  regionIndex: number,
  newRowStart: number,
): RepeatRowReassignPlan | null {
  const regions = table.repeatRegions;
  if (!regions || regionIndex < 0 || regionIndex >= regions.length) return null;
  const region = regions[regionIndex];
  const band = region.rowEnd - region.rowStart;
  const newRowEnd = newRowStart + band;
  // 새 밴드가 표 범위를 벗어나면 거부(no-op).
  if (newRowStart < 0 || newRowEnd >= table.rowCount) return null;
  const moves: RepeatRowReassignPlan['moves'] = [];
  const skips: RepeatRowReassignPlan['skips'] = [];
  if (newRowStart !== region.rowStart) {
    // 옛 밴드(rowStart~rowEnd) 안의 바인딩 셀만 이동 대상.
    for (const cell of table.cells) {
      if (cell.row < region.rowStart || cell.row > region.rowEnd) continue;
      if (!cell.binding) continue;
      const toRow = newRowStart + (cell.row - region.rowStart);
      const toCol = cell.col;
      const target = table.cells.find((c) => c.row === toRow && c.col === toCol);
      // 대상 셀이 옛 밴드 안(어차피 비워질 예정)이면 점유로 보지 않는다(밴드 겹침 이동 허용).
      const targetInOldBand =
        target !== undefined && target.row >= region.rowStart && target.row <= region.rowEnd;
      const occupied =
        target === undefined /* 병합에 덮여 셀 없음 */ ||
        (target.content?.length ?? 0) > 0 ||
        target.aggregate !== undefined ||
        target.compute !== undefined ||
        (target.binding !== undefined && !targetInOldBand);
      if (occupied) {
        skips.push({ fromRow: cell.row, fromCol: cell.col });
      } else {
        moves.push({ fromRow: cell.row, fromCol: cell.col, toRow, toCol, binding: cell.binding });
      }
    }
  }
  return { newRowStart, newRowEnd, moves, skips };
}

/**
 * 반복영역 밴드 행을 newRowStart로 재지정한다(밴드 크기 유지, 불변). planRepeatRegionRowReassign 계획대로
 * 옛 밴드 매핑 바인딩을 새 밴드로 이동(대상이 빈/정적 셀) 또는 옛 셀만 정적 환원(대상 점유 시)한 뒤,
 * region.rowStart/rowEnd를 갱신한다. regionIndex 무효/범위 밖이면 no-op(참조 동일).
 */
export function reassignRepeatRegionRow(
  t: ReportTemplate,
  tableId: string,
  regionIndex: number,
  newRowStart: number,
): ReportTemplate {
  return mapTable(t, tableId, (table) => {
    const plan = planRepeatRegionRowReassign(table, regionIndex, newRowStart);
    if (!plan) return table;
    const regions = table.repeatRegions;
    if (!regions) return table;
    const region = regions[regionIndex];
    // 위치 동일 + 이동/미지정 없음 → 무변경.
    if (
      plan.newRowStart === region.rowStart &&
      plan.moves.length === 0 &&
      plan.skips.length === 0
    ) {
      return table;
    }
    // 정적 환원할 옛 셀(moves.from + skips.from) / 바인딩 이식할 새 셀(moves.to).
    const clearKeys = new Set<string>([
      ...plan.moves.map((m) => `${m.fromRow}:${m.fromCol}`),
      ...plan.skips.map((s) => `${s.fromRow}:${s.fromCol}`),
    ]);
    const setMap = new Map<string, Binding>(
      plan.moves.map((m): [string, Binding] => [`${m.toRow}:${m.toCol}`, m.binding]),
    );
    const nextCells = table.cells.map((cell) => {
      const key = `${cell.row}:${cell.col}`;
      let next = cell;
      // 1) 옛 밴드 매핑 셀 → 정적 환원(binding 제거, 정적 빈 텍스트로).
      if (clearKeys.has(key)) {
        next = { ...next };
        delete next.binding;
        if (next.text === undefined) next.text = '';
      }
      // 2) 이동 대상 셀 → binding 이식(정적 text 제거).
      const toBinding = setMap.get(key);
      if (toBinding) {
        next = { ...next, binding: { ...toBinding } };
        delete next.text;
      }
      return next;
    });
    const nextRegions = regions.map((r, idx) =>
      idx === regionIndex ? { ...r, rowStart: plan.newRowStart, rowEnd: plan.newRowEnd } : r,
    );
    return { ...table, cells: nextCells, repeatRegions: nextRegions };
  });
}

// ============================================================
// removeNode — id 노드를 형제 배열에서 제거
// ============================================================

/**
 * id 노드를 그것이 속한 ComposerNode 배열(body / group.children / cell.content)에서 제거한다.
 *
 * 제거 대상에서 제외(무시):
 *   - repeat.template(단일 그룹) 자체 — 배열 원소가 아님
 *   - table 노드 자체가 아닌 table cell 자체(TableCell) — ComposerNode가 아님
 *   (해당 id가 이런 비-배열 위치만 가리키면 트리는 변경되지 않는다.)
 *
 * id가 없으면 동일 트리(참조 동일)를 반환.
 */
export function removeNode(t: ReportTemplate, id: string): ReportTemplate {
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next: ComposerNode[] = [];
    for (const node of nodes) {
      if (node.id === id) {
        changed = true;
        continue; // 제거
      }
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      next.push(mapped);
    }
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

// ============================================================
// moveNodeInSiblings — 같은 부모 형제 배열 내 순서 교환
// ============================================================

/**
 * id 노드를 같은 부모 형제 배열 안에서 한 칸 위(up)/아래(down)로 이동한다.
 * 경계(첫 원소 up / 마지막 원소 down)면 변경하지 않는다.
 * id가 없거나 경계면 동일 트리(참조 동일)를 반환.
 */
export function moveNodeInSiblings(
  t: ReportTemplate,
  id: string,
  dir: 'up' | 'down',
): ReportTemplate {
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    const idx = nodes.findIndex((n) => n.id === id);
    if (idx !== -1) {
      const swapWith = dir === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= nodes.length) return nodes; // 경계 무변경
      const next = nodes.slice();
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return next;
    }
    // 이 레벨에 없으면 각 노드 하위로 재귀
    let changed = false;
    const next = nodes.map((node) => {
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

// ============================================================
// insertNode — 노드 삽입(부모 그룹 지정 또는 body)
// ============================================================

/** insertNode 옵션 */
export interface InsertNodeOptions {
  /** 지정 시 해당 group/stack.children에 삽입(없으면 body). 이름은 back-compat 유지(group/stack 공통). */
  parentGroupId?: string;
  /** 삽입 위치(미지정 시 끝에 append) */
  index?: number;
}

/** 배열의 index 위치에 node 삽입(index 미지정/범위 밖이면 끝에 append) */
function insertAt(nodes: ComposerNode[], node: ComposerNode, index?: number): ComposerNode[] {
  const next = nodes.slice();
  if (index === undefined || index < 0 || index > next.length) {
    next.push(node);
  } else {
    next.splice(index, 0, node);
  }
  return next;
}

/**
 * node를 트리에 삽입한다.
 *   - opts.parentGroupId 지정: 해당 GroupNode.children에 삽입(group이 아니면 무변경)
 *   - 미지정: body에 삽입
 * opts.index로 위치 지정(미지정 시 append).
 * parentGroupId가 트리에 없거나 group이 아니면 동일 트리(참조 동일)를 반환.
 */
export function insertNode(
  t: ReportTemplate,
  node: ComposerNode,
  opts?: InsertNodeOptions,
): ReportTemplate {
  // body 직접 삽입
  if (!opts?.parentGroupId) {
    return { ...t, body: insertAt(t.body, node, opts?.index) };
  }

  const parentGroupId = opts.parentGroupId;
  const index = opts.index;

  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((n) => {
      // group/stack 모두 자식 컨테이너 — parentGroupId가 어느 쪽이든 children에 삽입.
      if (n.id === parentGroupId && (n.kind === 'group' || n.kind === 'stack')) {
        changed = true;
        return { ...n, children: insertAt(n.children, node, index) };
      }
      const mapped = mapChildren(n, mapList);
      if (mapped !== n) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t; // 대상 컨테이너 미발견 → 무변경
  return { ...t, body: nextBody };
}

// ============================================================
// insertNodeIntoCell — 표 셀 content 배열에 노드 삽입
// ============================================================

/**
 * 표 셀 content에 삽입 가능한 노드 kind 화이트리스트.
 * table/repeat/special은 제외한다 — 셀 안에 표/반복/특수 위젯을 중첩하지 않는다(불변 규약 유지).
 * admin(팔레트·셀 편집기 "요소 추가")이 재사용한다.
 */
export const CELL_INSERTABLE_KINDS = [
  'text',
  'image',
  'line',
  'rect',
  'checkbox',
  'choiceGroup',
  'spacer',
  'group',
  'stack',
] as const;

/** CELL_INSERTABLE_KINDS 원소 타입. */
export type CellInsertableKind = (typeof CELL_INSERTABLE_KINDS)[number];

/** kind가 셀 content에 삽입 가능한지(화이트리스트). */
export function isCellInsertableKind(kind: ComposerNode['kind']): kind is CellInsertableKind {
  return (CELL_INSERTABLE_KINDS as readonly string[]).includes(kind);
}

/**
 * tableId table의 (row,col) 셀 content 배열에 node를 삽입한 새 트리를 반환한다(불변).
 *
 * - table은 group/repeat.template/cell.content 내 중첩 위치까지 재귀 탐색(updateCell과 동일).
 * - 셀에 content가 없으면 생성한다. **기존 `text`(비어있지 않음)가 있으면** 그 text를 text 노드로
 *   승격해 content[0]에 두고(createDefaultNode('text') 기반) `cell.text`를 제거한다(기존 표시 유지).
 *   빈 text('' 또는 undefined)는 승격하지 않고 그냥 제거한다(표시 없음).
 * - node.kind가 CELL_INSERTABLE_KINDS(text|image|line|rect|checkbox|spacer|group|stack)가 아니면
 *   무변경(참조 동일)을 반환한다 — table|repeat|special은 셀 안 삽입 금지(불변 규약 유지).
 * - index로 content 배열 내 삽입 위치 지정(미지정/범위 밖이면 끝에 append).
 * - tableId가 없거나 그 노드가 table이 아니거나, (row,col) 셀이 없으면 무변경(참조 동일)을 반환한다.
 *
 * ⚠️ binding/compute/aggregate가 있는 셀에도 op 수준에서는 삽입을 허용한다(순수 함수). 다만 렌더
 *    우선순위가 content > text > aggregate > compute > binding이므로, content가 생기면 기존 값 표현
 *    (binding 등)이 가려진다. 값 셀 차단은 호출 측(admin)에서 판단한다.
 */
export function insertNodeIntoCell(
  t: ReportTemplate,
  tableId: string,
  row: number,
  col: number,
  node: ComposerNode,
  index?: number,
): ReportTemplate {
  if (!isCellInsertableKind(node.kind)) return t; // 허용 외 kind → 무변경

  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((n) => {
      if (n.id === tableId && n.kind === 'table') {
        let cellChanged = false;
        const nextCells = n.cells.map((cell) => {
          if (cell.row !== row || cell.col !== col) return cell;
          cellChanged = true;
          // 기존 content가 있으면 그대로 이어붙이고, 없으면 새로 만든다.
          // content가 없고 기존 text가 비어있지 않으면 text 노드로 승격(content[0]).
          let base: ComposerNode[];
          if (cell.content) {
            base = cell.content;
          } else if (cell.text !== undefined && cell.text !== '') {
            const promoted = createDefaultNode(
              'text',
              `${tableId}-r${row}-c${col}-text`,
            ) as TextNode;
            promoted.text = cell.text;
            base = [promoted];
          } else {
            base = [];
          }
          const nextContent = insertAt(base, node, index);
          const nextCell: TableCell = { ...cell, content: nextContent };
          delete nextCell.text; // text는 content로 흡수(승격했든 비어있었든 제거)
          return nextCell;
        });
        if (!cellChanged) return n; // 대상 셀 없음 → 무변경
        changed = true;
        return { ...n, cells: nextCells };
      }
      // 중첩(group/repeat.template/cell.content)에 table이 있을 수 있으므로 재귀.
      const mapped = mapChildren(n, mapList);
      if (mapped !== n) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t; // 대상 table/셀 미발견 → 무변경
  return { ...t, body: nextBody };
}

// ============================================================
// moveNodeTo — 노드를 임의 부모(body/group)의 index로 이동
// ============================================================

/** 노드(및 그 자식)에서 id 매칭 노드를 찾는다(이동 가드: 자손 검사용) */
function findNodeDeep(node: ComposerNode, id: string): ComposerNode | null {
  if (node.id === id) return node;
  switch (node.kind) {
    case 'group':
    case 'stack':
      return findInListDeep(node.children, id);
    case 'repeat':
      return findNodeDeep(node.template, id);
    case 'branch': {
      for (const slot of node.branches) {
        const found = findInListDeep(slot.children, id);
        if (found) return found;
      }
      return null;
    }
    case 'table': {
      for (const cell of node.cells) {
        if (cell.content) {
          const found = findInListDeep(cell.content, id);
          if (found) return found;
        }
      }
      return null;
    }
    default:
      return null;
  }
}

function findInListDeep(nodes: ComposerNode[], id: string): ComposerNode | null {
  for (const n of nodes) {
    const found = findNodeDeep(n, id);
    if (found) return found;
  }
  return null;
}

/** node가 자신 또는 자손에 targetId를 포함하는지(= target이 node 안에 있으면 이동 불가) */
function containsDeep(node: ComposerNode, targetId: string): boolean {
  return findNodeDeep(node, targetId) !== null;
}

/**
 * nodeId 노드를 현재 위치에서 떼어, targetParentId(group id; null/undefined면 body)의
 * children/body 배열 내 index 위치에 삽입한 새 트리를 반환한다(불변).
 *
 * - 같은 부모 내 index 재배치도 지원(예: 형제 재정렬).
 * - target group은 nodeId의 자손이면 안 됨(자기 자신/자기 자손으로 이동 차단 → 무변경).
 * - targetParentId가 지정됐는데 그게 group이 아니거나 부재면 무변경.
 * - nodeId가 트리에 없으면 무변경.
 * - 이동 대상 단위는 ComposerNode 배열의 원소(body/group.children)뿐.
 *   repeat.template(단일 그룹)·table cell 자체는 이동 대상에서 제외(removeNode 규약과 동일하게
 *   분리 단계에서 떼어지지 않으므로 무변경).
 *
 * index는 "노드를 떼어내기 전 기준"이 아니라 "떼어낸 뒤 target 배열의 삽입 위치"로 해석한다.
 * 같은 부모 내 재배치 시, 떼어낸 뒤 배열이 한 칸 줄어든 상태의 index를 그대로 사용한다(범위 밖이면 끝).
 */
export function moveNodeTo(
  t: ReportTemplate,
  nodeId: string,
  targetParentId: string | null | undefined,
  index: number,
): ReportTemplate {
  // 1) 이동할 노드를 찾는다(없으면 무변경).
  const moving = findInListDeep(t.body, nodeId);
  if (!moving) return t;

  // 2) target 컨테이너(group/stack) 검증 + 자기/자손 이동 차단.
  if (targetParentId != null) {
    const targetParent = findInListDeep(t.body, targetParentId);
    if (!targetParent || (targetParent.kind !== 'group' && targetParent.kind !== 'stack')) return t; // 컨테이너 아님/부재 → 무변경
    if (targetParentId === nodeId) return t; // 자기 자신으로 이동 금지
    if (containsDeep(moving, targetParentId)) return t; // 자손 컨테이너로 이동 금지(순환)
  }

  // 3) 트리에서 노드를 제거한다(removeNode와 동일 범위 — body/group.children/cell.content).
  const removed = removeNode(t, nodeId);
  if (removed === t) {
    // 제거되지 않음 = repeat.template/table cell 자체 등 비-배열 위치 → 이동 대상 제외(무변경).
    return t;
  }

  // 4) target 배열의 index 위치에 삽입한다.
  if (targetParentId == null) {
    return { ...removed, body: insertAt(removed.body, moving, index) };
  }

  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((n) => {
      if (n.id === targetParentId && (n.kind === 'group' || n.kind === 'stack')) {
        changed = true;
        return { ...n, children: insertAt(n.children, moving, index) };
      }
      const mapped = mapChildren(n, mapList);
      if (mapped !== n) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(removed.body);
  if (nextBody === removed.body) return t; // target 컨테이너 미발견(이론상 도달 안 함)
  return { ...removed, body: nextBody };
}

// ============================================================
// createDefaultNode — kind별 합리적 기본 노드 생성
// ============================================================

/**
 * 결재란(approvalBlock) 기본 구성 — SMS 네이티브 양식값(저장 px). createDefaultNode와
 * admin 속성 패널의 '초기화' 버튼이 공용으로 참조(값 드리프트 방지).
 * - 칸 너비 20.7mm(78.236px) / 단계명란 높이 ≈7.41mm(28px) / 서명란 10.58mm(40px) / 이름란 ≈6mm(22.677px)
 * - 패딩: 단계명란 th 3·10px / 서명·이름란 td 4·6px
 * - 테두리: 칸 외곽 1.5px #000(0.40mm) / 영역 구분선 1px #000(0.26mm) — 구 SMS .sms-approval-table
 */
export const DEFAULT_APPROVAL_CONFIG: ApprovalBlockConfig = {
  // 이름란 기본 해제(신규 결재란) — 필요 시 속성 패널에서 켠다. 기존 노드(showName 미지정)는
  // 렌더 폴백(?? true)이라 무영향(2026-07-09). 렌더 폴백 자체는 기존 템플릿 보존 위해 true 유지.
  showName: false,
  cellWidth: 78.236,
  stepLabelHeight: 28,
  signatureHeight: 40,
  nameHeight: 22.677,
  stepLabelPadding: { top: 3, right: 10, bottom: 3, left: 10 },
  signaturePadding: { top: 4, right: 6, bottom: 4, left: 6 },
  namePadding: { top: 4, right: 6, bottom: 4, left: 6 },
  cellBorder: '1.5px solid #000',     // 칸 외곽 0.40mm — 구 SMS .sms-approval-table 1.5px
  sectionDivider: '1px solid #000',   // 영역 구분선 0.26mm — 구 SMS 셀 1px
  // 글자색 #111(구 SMS 본문색) — 구 SMS .sms-print-report { color: #111 } 상속값
  stepLabelFont: { color: '#111111' }, // 단계명란 글자색
  nameFont: { color: '#111111' },      // 이름란 글자색
  // 채움색 기본 투명 — 단계명란 CSS 기본(.approval-cell-label background #f3f4f6 회색)을 덮어 투명으로.
  // 서명란/이름란은 CSS 기본이 이미 투명이나 일관성 위해 동일 지정.
  stepLabelRect: { fill: 'transparent' },
  signatureRect: { fill: 'transparent' },
  nameRect: { fill: 'transparent' },
};

/**
 * kind에 대한 합리적 기본 노드를 생성한다(주어진 id 사용).
 * special은 `special` 인자로 종류를 지정(미지정 시 'approvalBlock'). repeat는 아직 범위 외(throw).
 */
export function createDefaultNode(
  kind: ComposerNode['kind'],
  id: string,
  special?: SpecialKind,
): ComposerNode {
  switch (kind) {
    case 'text': {
      const node: TextNode = {
        id,
        kind: 'text',
        // 최소 높이 기본 0 — 텍스트는 내용 높이에 맞춘다(불필요한 빈 공간 방지). 필요 시 사용자가 지정.
        layout: { width: 'fill', minHeight: 0 },
        text: '새 텍스트',
      };
      return node;
    }
    case 'rect': {
      const node: RectNode = {
        id,
        kind: 'rect',
        layout: { width: 'fill', minHeight: 40 },
        style: { fill: '#f1f5f9', borderRadius: 4 },
      };
      return node;
    }
    case 'image': {
      const node: ImageNode = {
        id,
        kind: 'image',
        layout: { width: 'fill', minHeight: 80 },
        fit: 'contain',
      };
      return node;
    }
    case 'line': {
      const node: LineNode = {
        id,
        kind: 'line',
        layout: { width: 'fill', minHeight: 1 },
        orient: 'h',
      };
      return node;
    }
    case 'spacer': {
      const node: SpacerNode = {
        id,
        kind: 'spacer',
        layout: { width: 'fill', minHeight: 16 },
      };
      return node;
    }
    case 'checkbox': {
      // 박스+라벨이 한 시각 단위 — content-hug(width:'auto')로 라벨 폭만큼만 차지.
      const node: CheckboxNode = {
        id,
        kind: 'checkbox',
        layout: { width: 'auto', minHeight: 0 },
        label: '항목',
        boxSize: 13,
      };
      return node;
    }
    case 'choiceGroup': {
      // 선택지 그룹 — 목록·값·라벨은 저장하지 않고 렌더 시점에 dataDef options에서 파생.
      // content-hug(width:'auto')로 옵션 나열 폭만큼만 차지. binding(행 필드)은 속성 패널에서 지정.
      const node: ChoiceGroupNode = {
        id,
        kind: 'choiceGroup',
        layout: { width: 'auto', minHeight: 0 },
        boxSize: 13,
      };
      return node;
    }
    case 'group': {
      const node: GroupNode = {
        id,
        kind: 'group',
        layout: { width: 'fill' },
        direction: 'column',
        gap: 6,
        children: [],
      };
      return node;
    }
    case 'stack': {
      // z-레이어 오버레이 컨테이너. width fill + minHeight로 빈 상태에서도 영역 확보.
      // pageBreak는 부여하지 않는다 — 엔진(pageMeasure/pagePlan)이 소비하는 값은 'before'뿐이라
      // 'avoid'는 계획에 아무 영향이 없는 무효 값이었다(감사 2026-07-25).
      const node: StackNode = {
        id,
        kind: 'stack',
        layout: { width: 'fill', minHeight: 60 },
        children: [],
      };
      return node;
    }
    case 'table': {
      const node: TableNode = {
        id,
        kind: 'table',
        layout: { width: 'fill' },
        tablePolicy: 'rows',
        rowCount: 1,
        colCount: 2,
        cells: [
          { row: 0, col: 0, text: '', style: newCellStyle() },
          { row: 0, col: 1, text: '', style: newCellStyle() },
        ],
      };
      return node;
    }
    case 'special': {
      const specialKind: SpecialKind = special ?? 'approvalBlock';
      const node: SpecialNode = {
        id,
        kind: 'special',
        // approvalBlock은 content-hug(가로·세로)라 크기 layout 없이 가로 정렬(alignH)만 둔다(기본 우측=end).
        // 다른 special(annotatedPhoto/signaturePad)은 기존 fill+minHeight 박스 유지.
        // (결정 기록 2026-06-22-composer-approval-block-sizing-alignment B안)
        layout: specialKind === 'approvalBlock' ? { alignH: 'end' } : { width: 'fill', minHeight: 80 },
        special: specialKind,
        // approvalBlock도 StepperLayout 고정(variant 배선 제거 2026-06-23).
        // 결재란만 SMS 네이티브 양식 동형 기본값(칸 너비·각 높이·패딩)을 둔다(DEFAULT_APPROVAL_CONFIG).
        // 다른 special(annotatedPhoto/signaturePad)은 props 없음(undefined 유지).
        props:
          specialKind === 'approvalBlock'
            ? { approvalConfig: { ...DEFAULT_APPROVAL_CONFIG } }
            : undefined,
      };
      return node;
    }
    case 'branch': {
      // 조건 슬롯 2개 — 첫 슬롯은 condition '' 빈 문자열(운영자가 채울 자리), 둘째 슬롯은 condition
      // 미지정(else = 무조건 매칭). children은 각각 빈 배열. 슬롯 id는 branch id 접두로 트리 유일.
      // branch는 "투명 논리 컨테이너"라 자체 layout을 갖지 않는다(빈 객체) — 크기는 항상 활성
      // 슬롯 콘텐츠가 결정한다. 빈 슬롯 캔버스 가시성은 RenderTemplate의 renderEmptyContainerHint
      // 실효 40px floor가 커버하므로 minHeight/width 지정이 불필요하다(설계: content-hug 확장).
      const node: BranchNode = {
        id,
        kind: 'branch',
        layout: {},
        branches: [
          { id: `${id}-slot-0`, condition: '', children: [] },
          { id: `${id}-slot-1`, children: [] },
        ],
      };
      return node;
    }
    case 'repeat': {
      // 반복 노드 — over 미지정('', 운영자가 속성 패널에서 소스 선택). template은 **빈** group(column)
      // — branch 빈 슬롯과 동형 인터페이스(운영자가 요소를 직접 배치). 빈 template의 캔버스 가시성은
      // 편집 [{}] 뼈대 1회 렌더 + 빈 그룹 힌트(renderEmptyContainerHint 40px floor)가 커버한다.
      // id는 repeat id 접두로 트리 유일.
      const templateGroup: GroupNode = {
        id: `${id}-template`,
        kind: 'group',
        layout: { width: 'fill' },
        direction: 'column',
        gap: 6,
        children: [],
      };
      const node: RepeatNode = {
        id,
        kind: 'repeat',
        layout: { width: 'fill' },
        over: '',
        template: templateGroup,
      };
      return node;
    }
    default: {
      // exhaustiveness 가드
      const _exhaustive: never = kind;
      throw new Error(`createDefaultNode: 알 수 없는 kind '${String(_exhaustive)}'`);
    }
  }
}

// ============================================================
// 노드 묶기/풀기 ops — group/stack 컨테이너로 묶기·해제·종류 전환 (모두 불변)
// ============================================================
//
// 여러 형제 노드(같은 부모의 children 또는 body 원소)를 하나의 group/stack 컨테이너로 묶거나,
// 컨테이너를 풀어 자식을 그 자리에 펼치거나, 컨테이너 종류(group⇄stack)를 전환한다.
// 대상 배열 단위는 removeNode/insertNode와 동일하게 "ComposerNode 배열의 원소"다
// (body / group|stack.children / repeat.template.children / table cell.content).

/** 트리 전체의 노드 id를 수집한다(새 컨테이너 id 충돌 방지용). */
function collectAllIds(nodes: ComposerNode[], acc: Set<string>): void {
  for (const n of nodes) {
    acc.add(n.id);
    switch (n.kind) {
      case 'group':
      case 'stack':
        collectAllIds(n.children, acc);
        break;
      case 'repeat':
        acc.add(n.template.id);
        collectAllIds(n.template.children, acc);
        break;
      case 'branch':
        for (const slot of n.branches) collectAllIds(slot.children, acc);
        break;
      case 'table':
        for (const cell of n.cells) if (cell.content) collectAllIds(cell.content, acc);
        break;
      default:
        break;
    }
  }
}

/** 기존 id와 충돌하지 않는 `${kind}-${n}` 신규 컨테이너 id를 생성한다(admin 명명 규칙 동형). */
function genContainerId(existing: Set<string>, kind: 'group' | 'stack'): string {
  let n = 1;
  let id = `${kind}-${n}`;
  while (existing.has(id)) {
    n += 1;
    id = `${kind}-${n}`;
  }
  return id;
}

/** groupNodes 내부용 — id가 매칭된 형제 배열(원본 참조)과 매칭 id 집합. */
interface GroupMatch {
  parent: ComposerNode[];
  matched: Set<string>;
}

/**
 * idSet에 해당하는 노드가 든 ComposerNode 배열(형제 배열)들을 원본 참조 그대로 수집한다.
 * body / group|stack.children / repeat.template.children / table cell.content를 모두 순회한다.
 */
function collectGroupMatches(
  nodes: ComposerNode[],
  idSet: Set<string>,
  out: GroupMatch[],
): void {
  const matched = nodes.filter((n) => idSet.has(n.id));
  if (matched.length > 0) {
    out.push({ parent: nodes, matched: new Set(matched.map((n) => n.id)) });
  }
  for (const n of nodes) {
    switch (n.kind) {
      case 'group':
      case 'stack':
        collectGroupMatches(n.children, idSet, out);
        break;
      case 'repeat':
        collectGroupMatches(n.template.children, idSet, out);
        break;
      case 'branch':
        for (const slot of n.branches) collectGroupMatches(slot.children, idSet, out);
        break;
      case 'table':
        for (const cell of n.cells) if (cell.content) collectGroupMatches(cell.content, idSet, out);
        break;
      default:
        break;
    }
  }
}

/**
 * ids에 해당하는 형제 노드들을 하나의 group/stack 컨테이너로 묶는다(불변).
 *
 * - ids는 모두 같은 부모(body 또는 어떤 group/stack.children 등)의 형제여야 한다. 하나라도 다른
 *   부모에 있거나 트리에 없거나, ids.length<1이면 no-op(원본 반환).
 * - 새 컨테이너의 children은 형제 배열의 원래 순서를 보존한다. 컨테이너는 묶인 노드들의
 *   최소 index 위치에 삽입되고, 나머지 형제 순서는 유지된다.
 * - 컨테이너 노드는 createDefaultNode(kind)의 기본 layout/direction(group=column)을 재사용한다.
 */
export function groupNodes(
  t: ReportTemplate,
  ids: string[],
  kind: 'group' | 'stack',
): ReportTemplate {
  if (ids.length < 1) return t;
  const idSet = new Set(ids);
  const uniqueCount = idSet.size;

  // ids가 정확히 하나의 형제 배열에 전부 있어야 함(여러 배열에 흩어지면 no-op).
  const matches: GroupMatch[] = [];
  collectGroupMatches(t.body, idSet, matches);
  if (matches.length !== 1) return t;
  const target = matches[0];
  if (target.matched.size !== uniqueCount) return t; // 일부 id 부재/타 배열

  // 새 컨테이너 id + 노드(기본 layout/direction 재사용).
  const existing = new Set<string>();
  collectAllIds(t.body, existing);
  const containerId = genContainerId(existing, kind);
  const container = createDefaultNode(kind, containerId) as GroupNode | StackNode;

  // 대상 형제 배열 변환: 묶일 노드를 원래 순서로 컨테이너 children에 담고,
  // 컨테이너를 최소 index(=첫 묶임 노드) 위치에 삽입(나머지 형제 순서 보존).
  const buildGrouped = (nodes: ComposerNode[]): ComposerNode[] => {
    const grouped = nodes.filter((n) => idSet.has(n.id));
    const withChildren: ComposerNode = { ...container, children: grouped } as ComposerNode;
    const result: ComposerNode[] = [];
    let inserted = false;
    for (const n of nodes) {
      if (idSet.has(n.id)) {
        if (!inserted) {
          result.push(withChildren);
          inserted = true;
        }
        continue; // 나머지 묶임 노드는 컨테이너 안에 들어감
      }
      result.push(n);
    }
    return result;
  };

  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    if (nodes === target.parent) return buildGrouped(nodes);
    let changed = false;
    const next = nodes.map((node) => {
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

/**
 * id 컨테이너(group/stack)를 풀어, 그 children을 컨테이너가 있던 자리(같은 index)에 순서대로 펼친다(불변).
 * - id가 group/stack이 아니거나 트리에 없으면 no-op(원본 반환).
 * - 자식 id는 유지된다.
 */
export function ungroupNode(t: ReportTemplate, id: string): ReportTemplate {
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    const idx = nodes.findIndex((n) => n.id === id && (n.kind === 'group' || n.kind === 'stack'));
    if (idx !== -1) {
      const container = nodes[idx] as GroupNode | StackNode;
      return [...nodes.slice(0, idx), ...container.children, ...nodes.slice(idx + 1)];
    }
    let changed = false;
    const next = nodes.map((node) => {
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

/**
 * nodeId 노드를 그것을 직접 담고 있는 부모 컨테이너(group/stack 또는 branch 슬롯) 밖으로 한 단계
 * 승격시킨 새 body 배열을 반환한다(불변) — "그룹 밖으로 내보내기".
 *
 * 부모 유형별 동작:
 *  - group/stack.children 직속: 부모에서 제거 → **조부 배열의 그 컨테이너 바로 다음**에 삽입.
 *    제거 후 부모 children이 비면 **빈 컨테이너를 제거**한다(빈 껍데기 잔존 방지).
 *  - branch 슬롯(slot.children) 직속: 슬롯에서 제거 → **branch가 속한 배열의 branch 바로 다음**에 삽입.
 *    슬롯이 비어도 branch/슬롯은 **제거하지 않는다**(빈 슬롯 힌트 렌더가 별도 존재).
 *
 * 승격 대상에서 제외(무변경 → 참조 동일 반환):
 *  - body 직속(최상위) 노드 — 나갈 상위 컨테이너가 없음.
 *  - table 셀 content 직속 노드 — 셀 밖 내보내기는 범위 밖(호출 측 canMoveOut에서도 비활성).
 *  - nodeId가 트리에 없거나, 위 두 부모 유형이 아닌 위치(예: repeat.template 직속)만 가리키는 경우.
 *
 * 순회 범위는 findNodeById와 동일(group/stack.children · branch 슬롯 · repeat.template · 셀 content
 * 하강). 셀/repeat.template 안의 group·branch에서도 그 내부 승격은 수행하되 셀/repeat 경계를 넘어
 * 밖으로 빼내지는 않는다(그 컨테이너 배열 안에 머문다). 무변경 시 입력 배열 참조를 그대로 반환한다.
 */
export function moveNodeOutOfContainer(nodes: ComposerNode[], nodeId: string): ComposerNode[] {
  const mapList = (list: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const result: ComposerNode[] = [];
    for (const node of list) {
      // 1) 이 node가 nodeId를 직접 담는 group/stack이면 → 여기(list=조부 배열)서 승격.
      if (node.kind === 'group' || node.kind === 'stack') {
        const childIdx = node.children.findIndex((c) => c.id === nodeId);
        if (childIdx !== -1) {
          const promoted = node.children[childIdx];
          const remaining = node.children.filter((c) => c.id !== nodeId);
          // 남은 children이 있으면 컨테이너 유지, 비면 빈 껍데기이므로 제거(넣지 않음).
          if (remaining.length > 0) {
            result.push({ ...node, children: remaining } as ComposerNode);
          }
          result.push(promoted); // 컨테이너 바로 다음(제거됐다면 그 자리)
          changed = true;
          continue;
        }
      }
      // 2) 이 node가 nodeId를 직접 담는 branch면 → 여기(list=조부 배열)서 승격(슬롯이 비어도 branch 유지).
      if (node.kind === 'branch') {
        const slotIdx = node.branches.findIndex((s) => s.children.some((c) => c.id === nodeId));
        if (slotIdx !== -1) {
          const slot = node.branches[slotIdx];
          const promoted = slot.children[slot.children.findIndex((c) => c.id === nodeId)];
          const nextBranches = node.branches.map((s, i) =>
            i === slotIdx ? { ...s, children: s.children.filter((c) => c.id !== nodeId) } : s,
          );
          result.push({ ...node, branches: nextBranches });
          result.push(promoted); // branch 바로 다음
          changed = true;
          continue;
        }
      }
      // 3) 승격 지점이 아니면 하위 컨테이너로 재귀(children/branches/repeat.template/cell.content).
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      result.push(mapped);
    }
    return changed ? result : list;
  };

  return mapList(nodes);
}

/**
 * id 컨테이너의 종류를 group⇄stack으로 전환한다(불변).
 * - id가 group/stack이 아니면 no-op(원본 반환).
 * - →'group': direction이 없으면 'column'으로 설정(children/layout 등 보존).
 * - →'stack': direction 필드 제거(children/layout 등 보존).
 */
export function setContainerKind(
  t: ReportTemplate,
  id: string,
  kind: 'group' | 'stack',
): ReportTemplate {
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.id === id && (node.kind === 'group' || node.kind === 'stack')) {
        changed = true;
        if (kind === 'group') {
          const g = { ...node, kind: 'group' } as GroupNode;
          if (g.direction === undefined) g.direction = 'column';
          return g;
        }
        // stack은 base + children만 가진다. group 전용 필드(direction/gap/style)를
        // 남기면 stack↔group 왕복 시 옛 gap 등이 부활하므로 전환 시 모두 strip.
        const s = { ...node, kind: 'stack' } as StackNode;
        const groupOnly = s as {
          direction?: 'column' | 'row';
          gap?: number;
          style?: unknown;
        };
        delete groupOnly.direction;
        delete groupOnly.gap;
        delete groupOnly.style;
        return s;
      }
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

// ============================================================
// 분기 슬롯 ops (branch) — BranchNode.branches 슬롯 추가/삭제/이동/조건편집 (모두 불변)
// ============================================================
//
// branch 노드는 조건 슬롯(BranchSlot: id/condition?/children[]) 배열을 보유하며, 렌더는 첫 매칭
// 슬롯의 children만 그린다. 여기서는 그 슬롯 목록을 편집하는 불변 ops만 제공한다 — 슬롯 내부
// children(ComposerNode 배열)의 추가/삭제/이동/편집은 body/group.children와 동일하게 mapChildren
// 경유의 범용 ops(insertNodeIntoBranchSlot/removeNode/moveNodeInSiblings/updateNode)가 담당한다.

/** branchId인 branch 노드를 트리에서 찾아 transform을 적용한 새 트리를 반환(불변, 중첩 재귀). */
function mapBranch(
  t: ReportTemplate,
  branchId: string,
  transform: (branch: BranchNode) => BranchNode,
): ReportTemplate {
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.id === branchId && node.kind === 'branch') {
        const nextBranch = transform(node);
        if (nextBranch === node) return node; // transform이 무변경 신호
        changed = true;
        return nextBranch;
      }
      // 중첩(group/stack.children/repeat.template/cell.content/타 branch slot)에 branch가 있을 수
      // 있으므로 재귀.
      const mapped = mapChildren(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (nextBody === t.body) return t;
  return { ...t, body: nextBody };
}

/** branch 슬롯들의 id와 충돌하지 않는 `${branchId}-slot-${n}` 신규 슬롯 id를 생성한다. */
function genBranchSlotId(branch: BranchNode): string {
  const existing = new Set(branch.branches.map((s) => s.id));
  let n = branch.branches.length;
  let id = `${branch.id}-slot-${n}`;
  while (existing.has(id)) {
    n += 1;
    id = `${branch.id}-slot-${n}`;
  }
  return id;
}

/**
 * branchId branch에 빈 슬롯(condition '' 빈 문자열, children [])을 index 위치에 추가한다(불변).
 * index 미지정/범위 밖이면 끝에 append. branchId가 branch가 아니면 무변경(참조 동일).
 */
export function addBranchSlot(t: ReportTemplate, branchId: string, index?: number): ReportTemplate {
  return mapBranch(t, branchId, (branch) => {
    const slot: BranchSlot = { id: genBranchSlotId(branch), condition: '', children: [] };
    const next = branch.branches.slice();
    if (index === undefined || index < 0 || index > next.length) next.push(slot);
    else next.splice(index, 0, slot);
    return { ...branch, branches: next };
  });
}

/**
 * branchId branch에서 slotId 슬롯을 제거한다(불변).
 * - 마지막 1개만 남으면 거부(원본 반환, no-op) — branch는 슬롯 ≥1 유지.
 * - slotId가 없으면 무변경(참조 동일).
 */
export function removeBranchSlot(
  t: ReportTemplate,
  branchId: string,
  slotId: string,
): ReportTemplate {
  return mapBranch(t, branchId, (branch) => {
    if (branch.branches.length <= 1) return branch; // 최소 1 가드
    const next = branch.branches.filter((s) => s.id !== slotId);
    if (next.length === branch.branches.length) return branch; // slotId 부재 → 무변경
    return { ...branch, branches: next };
  });
}

/**
 * branchId branch에서 slotId 슬롯을 한 칸 위(up)/아래(down)로 이동한다(불변).
 * 경계(첫 up/마지막 down)거나 slotId 부재면 무변경(참조 동일).
 */
export function moveBranchSlot(
  t: ReportTemplate,
  branchId: string,
  slotId: string,
  dir: 'up' | 'down',
): ReportTemplate {
  return mapBranch(t, branchId, (branch) => {
    const idx = branch.branches.findIndex((s) => s.id === slotId);
    if (idx === -1) return branch;
    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= branch.branches.length) return branch; // 경계 무변경
    const next = branch.branches.slice();
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    return { ...branch, branches: next };
  });
}

/**
 * branchId branch에서 slotId 슬롯의 condition을 교체한다(불변).
 * - condition===undefined면 condition 속성 제거(=else 슬롯 무조건 매칭), 그 외면 그 값으로 설정.
 *   호출 측(admin)이 "빈 입력=undefined(else)로 정규화" 여부를 결정해 전달한다.
 * - slotId 부재/branch 아님이면 무변경(참조 동일).
 */
export function updateBranchSlotCondition(
  t: ReportTemplate,
  branchId: string,
  slotId: string,
  condition: string | undefined,
): ReportTemplate {
  return mapBranch(t, branchId, (branch) => {
    const idx = branch.branches.findIndex((s) => s.id === slotId);
    if (idx === -1) return branch;
    // 값 무변경이면 참조 유지(무의미 undo 스텝 방지). 빌더가 미완성 절을 추가/제거하면
    // serializeCondition이 그 절을 빼 같은 문자열을 재방출하는데(값 미입력 eq 등), 가드가 없으면
    // 매번 새 참조가 되어 apply가 화면 변화 없는 undo 스텝을 쌓는다(undo가 "안 먹는" 증상).
    const currentCondition = branch.branches[idx].condition;
    if (condition === undefined ? currentCondition === undefined : currentCondition === condition) {
      return branch;
    }
    const next = branch.branches.map((s) => {
      if (s.id !== slotId) return s;
      const updated: BranchSlot = { ...s };
      if (condition === undefined) delete updated.condition;
      else updated.condition = condition;
      return updated;
    });
    return { ...branch, branches: next };
  });
}

/**
 * branchId branch의 조건 데이터 바인딩 목록(conditionBindings)을 next로 교체한다(불변).
 * - next가 빈 배열이면 conditionBindings 속성을 제거(조건 데이터 없음), 그 외면 그 값으로 설정.
 * - branchId가 branch가 아니면 무변경(참조 동일).
 * (updateBranchSlotCondition/Label와 동일 패턴 — 편집은 이 op 경유로 undo 히스토리에 통합.)
 */
export function updateBranchConditionBindings(
  t: ReportTemplate,
  branchId: string,
  next: ConditionBinding[],
): ReportTemplate {
  return mapBranch(t, branchId, (branch) => {
    // 값 무변경이면 참조 유지(무의미 undo 스텝 방지). conditionBindings는 소규모 평문 배열이라
    // 직렬화 비교로 충분하다(키 순서 차이는 false-negative=옛 동작이라 안전, 실변경 누락은 없음).
    const current = branch.conditionBindings ?? [];
    if (JSON.stringify(current) === JSON.stringify(next)) return branch;
    const updated: BranchNode = { ...branch };
    if (next.length === 0) delete updated.conditionBindings;
    else updated.conditionBindings = next;
    return updated;
  });
}

/**
 * branchId branch에서 slotId 슬롯의 label(표시 이름)을 교체한다(불변).
 * - 빈 문자열('')이거나 undefined면 label 속성을 제거(미설정), 그 외면 그 값으로 설정.
 * - slotId 부재/branch 아님이면 무변경(참조 동일). (updateBranchSlotCondition와 동일 패턴)
 */
export function updateBranchSlotLabel(
  t: ReportTemplate,
  branchId: string,
  slotId: string,
  label: string | undefined,
): ReportTemplate {
  return mapBranch(t, branchId, (branch) => {
    const idx = branch.branches.findIndex((s) => s.id === slotId);
    if (idx === -1) return branch;
    // 정규화(빈문자/undefined→미설정) 후 값 무변경이면 참조 유지(무의미 undo 스텝 방지).
    const currentLabel = branch.branches[idx].label;
    const clearing = label === undefined || label === '';
    if (clearing ? currentLabel === undefined : currentLabel === label) {
      return branch;
    }
    const next = branch.branches.map((s) => {
      if (s.id !== slotId) return s;
      const updated: BranchSlot = { ...s };
      if (label === undefined || label === '') delete updated.label;
      else updated.label = label;
      return updated;
    });
    return { ...branch, branches: next };
  });
}

/**
 * branchId branch의 slotId 슬롯 children 배열에 node를 삽입한 새 트리를 반환한다(불변).
 * index 미지정/범위 밖이면 끝에 append. branchId가 branch가 아니거나 slotId 부재면 무변경(참조 동일).
 * (팔레트/'요소 추가'가 활성 슬롯에 삽입할 때 쓴다 — cell 삽입(insertNodeIntoCell) 대응.)
 */
export function insertNodeIntoBranchSlot(
  t: ReportTemplate,
  branchId: string,
  slotId: string,
  node: ComposerNode,
  index?: number,
): ReportTemplate {
  return mapBranch(t, branchId, (branch) => {
    let changed = false;
    const next = branch.branches.map((s) => {
      if (s.id !== slotId) return s;
      changed = true;
      return { ...s, children: insertAt(s.children, node, index) };
    });
    if (!changed) return branch; // slotId 부재 → 무변경
    return { ...branch, branches: next };
  });
}

/**
 * mapChildren과 동일하나 **table 셀 content로는 하강하지 않는다** — branch 감싸기(wrapNodeInBranch) 전용.
 * branch는 CELL_INSERTABLE_KINDS가 아니라 셀 content에 넣을 수 없으므로, 셀 안 노드는 감싸기 대상에서 제외한다.
 */
function mapContainerChildrenNoCell(
  node: ComposerNode,
  mapList: (nodes: ComposerNode[]) => ComposerNode[],
): ComposerNode {
  switch (node.kind) {
    case 'group':
    case 'stack': {
      const nextChildren = mapList(node.children);
      if (nextChildren === node.children) return node;
      return { ...node, children: nextChildren };
    }
    case 'repeat': {
      const nextTemplate = mapContainerChildrenNoCell(node.template, mapList) as GroupNode;
      if (nextTemplate === node.template) return node;
      return { ...node, template: nextTemplate };
    }
    case 'branch': {
      let changed = false;
      const nextBranches = node.branches.map((slot) => {
        const nextChildren = mapList(slot.children);
        if (nextChildren === slot.children) return slot;
        changed = true;
        return { ...slot, children: nextChildren };
      });
      if (!changed) return node;
      return { ...node, branches: nextBranches };
    }
    // table: 셀 content로 하강하지 않음(branch 감싸기 거부 — 셀엔 branch 삽입 불가).
    default:
      return node;
  }
}

/**
 * nodeId 노드를 그 자리에서 branch로 감싼다(불변) — 슬롯0(condition '' + children [노드]) + 슬롯1(else 빈).
 * "단일 조건부 표시" 원클릭 진입점(노드별 condition 폐지 후 대체 UX). branch layout은 createDefaultNode('branch')와 동형.
 *
 * 대상 위치: body / group·stack.children / repeat.template.children / 타 branch slot.children.
 * ⚠️ table 셀 content 안의 노드는 감싸기 거부(no-op) — branch는 CELL_INSERTABLE_KINDS가 아니라 셀에 삽입할 수 없다.
 * nodeId가 트리에 없거나(또는 셀 content 안에만 존재하면) 무변경(참조 동일)을 반환한다.
 */
export function wrapNodeInBranch(t: ReportTemplate, nodeId: string): ReportTemplate {
  const existing = new Set<string>();
  collectAllIds(t.body, existing);
  if (!existing.has(nodeId)) return t; // 트리에 없음 → no-op
  // 새 branch id(기존 id와 충돌 회피). 슬롯 id는 branch id 접두라 branch id만 fresh면 슬롯 id도 유일.
  let n = 1;
  let branchId = `branch-${n}`;
  while (existing.has(branchId)) {
    n += 1;
    branchId = `branch-${n}`;
  }

  let wrapped = false;
  const mapList = (nodes: ComposerNode[]): ComposerNode[] => {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.id === nodeId) {
        wrapped = true;
        changed = true;
        const branch: BranchNode = {
          id: branchId,
          kind: 'branch',
          // branch는 투명 논리 컨테이너라 자체 layout 없음(createDefaultNode('branch')와 동일 빈 객체).
          // 크기는 활성 슬롯 콘텐츠가 결정 — 렌더가 layout을 무시하므로 값 채움이 무의미하다.
          layout: {},
          branches: [
            { id: `${branchId}-slot-0`, condition: '', children: [node] },
            { id: `${branchId}-slot-1`, children: [] },
          ],
        };
        return branch;
      }
      // 자식 컨테이너로 하강 — 단, table 셀 content는 제외(셀 안 노드는 감싸기 불가).
      const mapped = mapContainerChildrenNoCell(node, mapList);
      if (mapped !== node) changed = true;
      return mapped;
    });
    return changed ? next : nodes;
  };

  const nextBody = mapList(t.body);
  if (!wrapped) return t; // 셀 content 안에만 존재 등 → 감싸지 못함
  return { ...t, body: nextBody };
}
